import React, { type FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Task } from '@repo/shared';
import { Cog } from 'lucide-react';

import LanguageCombobox from '@/components/language-combobox';
import ModelsCombobox from '@/components/models-combobox';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Slider } from '@/components/ui/slider';
import { useOpen } from '@/hooks/use-open';
import InstructionsDialog from '@/routes/(app)/(playground)/(text-translation)/components/instructions-dialog';
import type { CreateTextTranslationWithTextSchema } from '@/routes/(app)/(playground)/(text-translation)/lib/schema';

interface ParametersCardProps {
  className?: string;
}

const ParametersCard: FC<ParametersCardProps> = ({ className }) => {
  const { t } = useTranslation();
  const form = useFormContext<CreateTextTranslationWithTextSchema>();
  const { open, onOpenChange } = useOpen({ defaultOpen: false });

  const handleOpenInstructions = useCallback(() => {
    onOpenChange(true);
  }, [onOpenChange]);

  const temperature = form.watch('temperature');

  return (
    <>
      <Card
        data-card
        className={className}>
        <CardContent className='flex flex-col gap-4'>
          <div className='flex gap-4'>
            <FormField
              control={form.control}
              name='sourceLanguageCode'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.textTranslation.form.sourceLanguageCode.label')}
                  </FormLabel>
                  <FormControl>
                    <LanguageCombobox
                      {...field}
                      placeholder={t(
                        'tasks.textTranslation.form.sourceLanguageCode.placeholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='targetLanguageCode'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.textTranslation.form.targetLanguageCode.label')}
                  </FormLabel>
                  <FormControl>
                    <LanguageCombobox
                      {...field}
                      placeholder={t(
                        'tasks.textTranslation.form.targetLanguageCode.placeholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name='models'
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('tasks.textTranslation.form.models.label')}
                </FormLabel>
                <FormControl>
                  <ModelsCombobox
                    className='max-w-[350px]'
                    task={Task.TEXT_TRANSLATION}
                    values={field.value}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='flex items-center gap-8'>
            <FormField
              control={form.control}
              name='temperature'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('playground.textTranslation.temperature.label')}
                  </FormLabel>
                  <FormControl>
                    <div className='flex items-center gap-1'>
                      <span className='text-muted-foreground text-sm'>
                        {temperature}
                      </span>
                      <Slider
                        className='w-[300px]'
                        min={0}
                        max={1}
                        step={0.1}
                        value={field.value}
                        onValueChange={field.onChange}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              variant={'outline'}
              className='w-fit'
              onClick={handleOpenInstructions}>
              <Button.Icon side='left'>
                <Cog />
              </Button.Icon>
              {t('playground.textTranslation.instructions.label')}
            </Button>
          </div>
        </CardContent>
      </Card>
      <InstructionsDialog
        open={open}
        onOpenChange={onOpenChange}
      />
    </>
  );
};

export default ParametersCard;
